import { useState } from 'react';
import React from 'react';
import { useRulesEngine } from '../../../../../../../hooks/useRulesEngine';
import { useCategoryRulesEngine } from '../../../../../../../hooks/useCategoryRulesEngine';
import { RuleRowProps } from './types';
import { RuleCondition, SecurityType } from '../../../../../../../types';

export const useRuleRow = (props: RuleRowProps) => {
  const { rule, sectionId, categoryId } = props;

  // Conditionally use the correct engine
  const regularEngine = useRulesEngine(sectionId);
  const categoryEngine = useCategoryRulesEngine(sectionId, categoryId!);
  const {
    addLabelToColumn,
    removeLabelFromColumn,
    updateRuleDetails,
    addColumn,
    removeColumn,
    updateColumnSource,
    addSecurityTypeColumn,
    updateColumnSecurityType,
  } = categoryId ? categoryEngine : regularEngine;

  // UI state
  const [isExpanded, setIsExpanded] = useState(true); // Default expanded
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<string | null>(null);

  // Toggle expanded state
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle adding label to a specific column
  const handleAddLabel = (columnId: string, value: string) => {
    addLabelToColumn(rule.id, columnId, value);
  };

  // Handle column source change
  const handleColumnSourceChange = (
    columnId: string,
    newSource: string | null
  ) => {
    updateColumnSource(rule.id, columnId, newSource || '');
  };

  // State for the "Add Condition" menu
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOpenAddMenu = (e: React.MouseEvent<HTMLElement>) => setAddMenuAnchor(e.currentTarget);
  const handleCloseAddMenu = () => setAddMenuAnchor(null);

  // Handle adding a file column (existing behavior)
  const handleAddFileColumn = () => {
    addColumn(rule.id);
    handleCloseAddMenu();
  };

  // Handle adding a security type condition column
  const handleAddSecurityTypeColumn = () => {
    addSecurityTypeColumn(rule.id);
    handleCloseAddMenu();
  };

  // Handle security type value change on a SECURITY_TYPE column
  const handleSecurityTypeChange = (columnId: string, value: SecurityType) => {
    updateColumnSecurityType(rule.id, columnId, value);
  };

  // Handle remove column - show confirmation dialog
  const handleRemoveColumn = (columnId: string) => {
    setColumnToDelete(columnId);
    setConfirmDialogOpen(true);
  };

  // Confirm column deletion
  const confirmRemoveColumn = () => {
    if (columnToDelete) {
      removeColumn(rule.id, columnToDelete);
      setColumnToDelete(null);
      setConfirmDialogOpen(false);
    }
  };

  // Cancel column deletion
  const cancelRemoveColumn = () => {
    setColumnToDelete(null);
    setConfirmDialogOpen(false);
  };

  // Handle condition change at a specific index
  const handleConditionChange = (
    conditionIndex: number,
    newCondition: RuleCondition
  ) => {
    const newConditions = [...rule.conditions];
    newConditions[conditionIndex] = newCondition;
    updateRuleDetails(rule.id, { conditions: newConditions });
  };

  // Handle label removal for a specific column
  const handleRemoveLabel = (columnId: string, labelId: string) => {
    removeLabelFromColumn(rule.id, columnId, labelId);
  };

  return {
    isExpanded,
    toggleExpanded,
    confirmDialogOpen,
    columnToDelete,
    handleAddLabel,
    handleColumnSourceChange,
    addMenuAnchor,
    handleOpenAddMenu,
    handleCloseAddMenu,
    handleAddFileColumn,
    handleAddSecurityTypeColumn,
    handleRemoveColumn,
    confirmRemoveColumn,
    cancelRemoveColumn,
    handleConditionChange,
    handleRemoveLabel,
    handleSecurityTypeChange,
  };
};
